import React, { useEffect, useState } from 'react';

import Seo from '../ui/Seo';
import Reveal from '../ui/Reveal';
import PageHero from '../sections/PageHero';
import TradeTicker from '../sections/TradeTicker';
import SectionHead from '../sections/SectionHead';
import Cta from '../sections/Cta';
import MediaGallery from '../sections/MediaGallery';
import { ProjectGrid, ProjectModal } from '../sections/ProjectCards';
import Loading from './loading';
import { fetchInstagramMedia } from '../ui/instagram';
import { residentialProjects, commercialProjects, additionalProjects } from '../data/projects.js';
import { contact, site } from '../data/site';

const FEED = 'https://api.smart-tech.solutions/getSESInstagramMedia';

/* Each category grid opens collapsed to this many cards, with a button to
   reveal the rest, so neither section buries the one below it. */
const SECTION_PREVIEW_COUNT = 8;

/* One category of landmark work: a heading, its lead-in, a collapsed grid
   and a show-all toggle. Rendering an empty category would leave a bare
   heading, so a category with no projects renders nothing at all. */
function ProjectCategory({ title, noun, eyebrow, lead, items, onSelect, children }) {
    const [showAll, setShowAll] = useState(false);

    if (items.length === 0) return null;

    const visible = showAll ? items : items.slice(0, SECTION_PREVIEW_COUNT);

    return (
        <section className="section section--ruled">
            <div className="shell">
                <SectionHead eyebrow={eyebrow} title={title} lead={lead} />

                <div style={{ marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                    <ProjectGrid items={visible} onSelect={onSelect} />
                </div>

                {items.length > SECTION_PREVIEW_COUNT && (
                    <div style={{ textAlign: 'center', marginTop: 'clamp(2rem, 4vw, 3rem)' }}>
                        <button
                            type="button"
                            className="btn btn--ghost"
                            onClick={() => setShowAll(!showAll)}
                        >
                            {showAll ? `SHOW FEWER ${noun}` : `SHOW ALL ${items.length} ${noun}`}
                        </button>
                    </div>
                )}

                {children}
            </div>
        </section>
    );
}

export default function Portfolio() {
    const [selectedProject, setSelectedProject] = useState(null);
    const [media, setMedia] = useState([]);
    const [state, setState] = useState('loading');

    useEffect(() => {
        const controller = new AbortController();

        fetchInstagramMedia(FEED, { signal: controller.signal })
            .then(items => {
                setMedia(items);
                setState('ready');
            })
            .catch(error => {
                if (error.name !== 'AbortError') setState('failed');
            });

        return () => controller.abort();
    }, []);

    return (
        <>
            <Seo
                title="Portfolio"
                description="Explore our portfolio of completed electrical projects across South Florida, separated into residential and mixed-use towers and commercial, hospitality and institutional work."
                keywords="residential electrical projects, commercial electrical projects, condominium towers, hotels, restaurants, electrical contractor Florida, project portfolio"
                path="/portfolio"
                schema={{
                    '@type': 'CollectionPage',
                    name: 'Portfolio',
                    description: 'Landmark electrical projects delivered across South Florida.',
                    url: `${site.origin}/portfolio`
                }}
            />

            {selectedProject && (
                <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
            )}

            <PageHero
                eyebrow="Portfolio"
                title="Landmark work, across South Florida."
                lead="Delivered by our executive leadership and grouped by the two sides of our practice. Select any project to see its unit count, general contractor and developer."
                crumbs={[{ label: 'Portfolio' }]}
            />

            <TradeTicker />

            <ProjectCategory
                eyebrow="Towers"
                title="Residential & Mixed-Use"
                noun="RESIDENTIAL PROJECTS"
                lead="Oceanfront condominium towers, rental and mixed-use communities, and senior living, delivered for developers including The Related Group, Dezer Development, Property Markets Group, Fortune International and Chateau Group."
                items={residentialProjects}
                onSelect={setSelectedProject}
            />

            <ProjectCategory
                eyebrow="Hospitality & civic"
                title="Commercial, Hospitality & Institutional"
                noun="COMMERCIAL PROJECTS"
                lead="Flagship hotels, signature restaurants, offices, retail, entertainment venues and public buildings, built alongside general contractors including Suffolk, Coastal, Plaza, Beauchamp and John Moriarty & Associates."
                items={commercialProjects}
                onSelect={setSelectedProject}
            >
                <div style={{ marginTop: 'clamp(3rem, 7vw, 5rem)' }}>
                    <Reveal y={20}>
                        <span className="t-eyebrow t-eyebrow--accent">Also delivered</span>
                    </Reveal>

                    <h3 className="t-h3" style={{ margin: '1.1rem 0 1.75rem' }}>
                        Additional Completed Work
                    </h3>

                    <ul className="worklist">
                        {additionalProjects.map(item => (
                            <li key={item.name}>
                                {item.name}
                                <span>{item.text}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </ProjectCategory>

            <section className="section section--raised">
                <div className="shell">
                    <SectionHead
                        eyebrow="Instagram"
                        title="From the Field"
                        lead="Straight from our feed, newest first."
                        center
                    />

                    <div style={{ marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                        {state === 'loading' && <Loading />}

                        {state === 'failed' && (
                            <p className="gallery__empty">
                                The feed could not be loaded right now. See the latest work on{' '}
                                <a className="link" href={contact.instagram} target="_blank" rel="noopener noreferrer">
                                    Instagram
                                </a>.
                            </p>
                        )}

                        {state === 'ready' && (
                            <MediaGallery
                                items={media}
                                emptyMessage="There is nothing here yet. Check back shortly."
                            />
                        )}
                    </div>
                </div>
            </section>

            <Cta
                eyebrow="Start a project"
                title="Put us on your next one."
                lead="Tell us about the programme: scope, schedule, and where you are in preconstruction. We will tell you what we would do differently."
            />
        </>
    );
}
